/**
 * GoldFire Network (https://goldfire.me)
 * Copyright (c) 2012 - 2020, GoldFire Studios, Inc.
 * http://goldfirestudios.com
 */

const moment = require('moment');
const Emoji = require('../store/emoji');

/**
 *
 *
 * Internal Methods.
 *
 *
 */

/**
 * Reformats moment's fromNow output to GFN style
 * e.g. '10 minutes' -> '10m' or 'an hour ago' -> '1h'
 * @param {String} str String to be reformatted
 * @return {String}
 */
const formatFromNow = (str) => {
  const spaceIndex = str.indexOf(' ');
  let value = str.slice(0, spaceIndex);
  value = (value === 'a' || value === 'an') ? 1 : value; // in e.g. 'a minute ago', 'a' becomes '1'
  const units = str.slice(spaceIndex + 1, spaceIndex + 2);

  return value + units;
};

/**
 * Convert a GFN ID into a default avatar of a certain color.
 * @param  {String} id GFN ID
 * @return {String}    URL to avatar
 */
const idToAvatar = (uid) => {
  uid = `${uid}`;

  const colors = {
    0: '00',
    1: '00',
    2: '01',
    3: '01',
    4: '02',
    5: '02',
    6: '03',
    7: '03',
    8: '04',
    9: '04',
    a: '05',
    b: '05',
    c: '06',
    d: '06',
    e: '07',
    f: '07',
    g: '08',
    h: '08',
    i: '09',
    j: '09',
    k: '10',
    l: '10',
    m: '11',
    n: '11',
    o: '12',
    p: '12',
    q: '13',
    r: '13',
    s: '14',
    t: '14',
    u: '15',
    v: '15',
    w: '16',
    x: '16',
    y: '17',
    z: '17',
  };

  const color = (colors[uid[uid.length - 1].toLowerCase()] || '00');

  return `avatar${color}.jpg`;
};

/**
 * Takes a string and truncates it based on the passed length limit.
 * @param  {String} msg   Text to truncate.
 * @param  {Number} limit Character limit.
 * @return {String}       Truncated string.
 */
const shortMessage = (msg, limit = 30) => (msg.length > limit ? `${msg.slice(0, limit - 3)}...` : msg);

/**
 * Convert the emoji categories into a single list.
 * @return {Object}
 */
const emojiList = () => {
  const list = {};
  Object.keys(Emoji).forEach((cat) => {
    Object.assign(list, Emoji[cat]);
  });

  return list;
};

/**
 *
 *
 * Exported Methods.
 *
 *
 */

module.exports = {
  /**
   * Reformats a timestamp to GFN style
   * e.g. 'now', '10m', 'Aug 20'
   * @param {Timestamp} timestamp Timestamp to be reformatted
   * @param {Boolean} includeYear Set true to include year in long date.
   * @param {String} yearFormat Specify the format to use for year.
   * @return {String}
   */
  formatTimestamp: (timestamp, includeYear, yearFormat) => {
    const isDate = typeof timestamp === 'number' || !isNaN(new Date(timestamp).getTime());
    if (!isDate) {
      return timestamp;
    }

    const time = moment(timestamp);
    const oneMinuteAgo = moment().subtract(1, 'minutes');
    const oneHourAgo = moment().subtract(1, 'hours');
    const oneDayAgo = moment().subtract(1, 'days');
    const isThisYear = moment().diff(time) <= 31536000000;

    if (time.isAfter(oneMinuteAgo)) {
      return 'now';
    }
    if (time.isAfter(oneHourAgo)) {
      return formatFromNow(time.fromNow());
    }
    if (time.isAfter(oneDayAgo)) {
      return formatFromNow(time.fromNow());
    }

    const format = yearFormat || 'MMM D, YYYY';

    return time.format(includeYear || !isThisYear ? format : 'MMM D');
  },

  /**
   * Formats a number to display commas
   * @param {Number}
   * @return {String}
   */
  numberWithCommas: number => `${number}`.replace(/\B(?=(\d{3})+(?!\d))/g, ','),

  /**
   * Defined above.
   */
  shortMessage,

  /**
   * Take an array of usernames and turn it into a shortened string.
   * @param  {Array} players Usernames in list.
   * @return {String}         Truncated list of usernames.
   */
  shortUsernames: (players) => {
    return players.reduce((abb, player) => `${abb}${player.user}, `, '').slice(0, -2);
  },

  /**
   * Pass in a filename and get an emoji code back.
   * @param  {String} val Emoji filename.
   * @return {String}     Emoji code.
   */
  emojiFromValue: (val) => {
    const list = emojiList();
    const keys = Object.keys(list);

    // Loop through list until we find a match.
    for (let i = 0; i < keys.length; i += 1) {
      if (list[keys[i]] === val) {
        return keys[i];
      }
    }

    return '';
  },

  /**
   * Take a string as input and convert any emoji's to image tags.
   * @param  {String} string Input string.
   * @return {String}        Formatted string.
   */
  convertEmoji: (string) => {
    let newString = string.toString();
    let emojis = newString.match(/:(.*?):/g) || [];

    // Only process the emojis if we have some.
    if (emojis.length > 0) {
      // Convert the emoji categories into a single list.
      const list = emojiList();

      // Remove emojis that aren't valid.
      emojis = emojis.filter(emoji => list[emoji]);

      // Replace the emojis with images.
      emojis.forEach((emoji) => {
        const img = `https://cdn.jsdelivr.net/emojione/assets/svg/${list[emoji]}.svg`;
        newString = newString.replace(new RegExp(emoji, 'ig'), `<img src="${img}" class="emoji">`);
      });
    }

    return newString;
  },

  /**
   * Formats a number to display as a percentage
   * @param {Number}
   * @return {String}
   */
  percentFromDecimal: number => (typeof number !== 'number' ? number : `${number * 100}%`),

  /**
   * Read cookie value with a specific name.
   * @param  {String} name Name to read.
   * @return {String}      Cookie value.
   */
  readCookie: (name) => {
    if (typeof document === 'undefined') {
      return null;
    }

    const nameEQ = `${name}=`;
    const ca = document.cookie.split(';');

    for (let i = 0; i < ca.length; i += 1) {
      let c = ca[i];
      while (c.charAt(0) === ' ') {
        c = c.substring(1, c.length);
      }

      if (c.indexOf(nameEQ) === 0) {
        return decodeURIComponent(c.substring(nameEQ.length, c.length));
      }
    }

    return null;
  },

  /**
   * Set a cookie with name, value and length.
   * @param  {String} name  Name of cookie.
   * @param  {String} value Value to set.
   * @param  {Number} len   Length in ms.
   */
  setCookie: (name, value, len) => {
    let expires = '';

    if (len) {
      expires = `expires=${new Date(Date.now() + len).toUTCString()}; `;
    }

    document.cookie = `${name}=${value}; ${expires}path=/`;
  },

  /**
   * Returns number of invisible divs to append to last row to force flex-box to space divs evenly.
   * See: http://stackoverflow.com/questions/40936543/left-align-last-row-of-flexbox-using-space-between-and-margins
   * @param {Integer} numElements The number of elements you're displaying.
   * @param {Number} element Width Width, in pixels, of each element.
   * @param {Number} spaceBetween Width, in pixels. Your minimum target margin between items.
   * (2x the margin on each individual item.)
   * @param {Number} containerWidth Width, in pixels, of the containing element.
   */
  getNumPhantomElements: ({numElements, elementWidth, spaceBetween, containerWidth}) => {
    const elementsPerRow = Math.floor(containerWidth / (elementWidth + spaceBetween));
    const elementsInLastRow = numElements % elementsPerRow;
    const numPhantomElements = elementsPerRow - elementsInLastRow;

    return numPhantomElements;
  },

  /**
   * Get a user's avatar based on the user ID and avatar counter.
   * @param  {String} uid    User ID.
   * @param  {Number} avatar Avatar counter.
   * @return {String}        Avatar file.
   */
  avatarFile: (uid, avatar) => (avatar > 0 ? `${uid}.png?v=${avatar}` : idToAvatar(uid)),

  /**
   * Convert an avatar filename into a full URL for rendering.
   * @param  {String} avatar Avatar filename.
   * @return {String}        Avatar URL.
   */
  avatarUrl: avatar => `https://cdn.goldfire.me/images/avatars/${avatar}`,

  /**
   * Convert a medal number into a full URL for rendering.
   * @param  {Number} img
   * @param  {Number} gid   Game ID.
   * @return {String}       Medal URL.
   */
  medalUrl: (gid, img) => `https://cdn.goldfire.me/images/medals/${gid}/${img}.png`,

  /**
   * Debounce method to slow down execution of costly actions.
   * @param  {Function} func Function to debounce.
   * @param  {Number} wait Wait time in MS.
   */
  debounce: (func, wait) => {
    let timeout;

    return function debounced(...args) {
      const later = () => {
        timeout = null;
        func.apply(this, args);
      };

      clearTimeout(timeout);
      timeout = setTimeout(later, wait);

      if (!timeout) {
        func.apply(this, args);
      }
    };
  },

  /**
   * Throttle method to limit execution of costly actions.
   * Adapted from: https://remysharp.com/2010/07/21/throttling-function-calls
   * @param  {Function} func Function to throttle.
   * @param  {Number} wait   Wait time in MS.
   * @param  {Object} scope  Execution context.
   */
  throttle: function throttle(func, wait = 250, scope) {
    let last;
    let deferTimer;

    return function throttled(...args) {
      const now = Date.now();
      const context = scope || this;

      if (last && now < last + wait) {
        clearTimeout(deferTimer);
        deferTimer = setTimeout(() => {
          last = now;
          func.apply(context, args);
        }, wait);
      } else {
        last = now;
        func.apply(context, args);
      }
    };
  },

  /**
   * Check if the current device is mobile.
   * @param  {String} ua (optional) User agent.
   * @return {Boolean}
   */
  isMobile: (ua) => {
    if (ua || (typeof window !== 'undefined' && typeof window.navigator !== 'undefined')) {
      const mobile = /iPhone|iPad|iPod|Android|BlackBerry|BB10|Silk|Mobi/i;
      return mobile.test(ua || window.navigator.userAgent);
    }

    return false;
  },

  /**
   * Check if this is an iOS device.
   * @param  {String} ua (optional) User agent.
   * @return {Boolean}
   */
  iOSDevice: (ua) => {
    if (ua || (typeof window !== 'undefined' && typeof window.navigator !== 'undefined')) {
      const mobile = /iPhone|iPad|iPod/i;
      return mobile.test(ua || window.navigator.userAgent);
    }

    return false;
  },

  /**
   * Make sure all links have a _blank target.
   * @param  {DOMElement} el The parent DOM.
   */
  linkTargetBlank: (el) => {
    const tags = el.getElementsByTagName('a');
    const links = Array.from(tags);

    // Add the attribute to each link.
    links.forEach((a) => {
      a.setAttribute('target', '_blank');
    });
  },

  /**
   * Check if the passed country code is a GDPR country.
   * @param  {String} country Country ISO code.
   * @return {Boolean}
   */
  isGdpr: (countryCode) => {
    const countries = [
      'AT',
      'BE',
      'BG',
      'HR',
      'CY',
      'CZ',
      'DK',
      'EE',
      'FI',
      'FR',
      'DE',
      'GR',
      'HU',
      'IE',
      'IT',
      'LV',
      'LT',
      'LU',
      'MT',
      'NL',
      'PL',
      'PT',
      'RO',
      'SK',
      'SI',
      'ES',
      'SE',
      'GB',
    ];

    return countries.includes(countryCode);
  },
};
